/**
 * PipelineRealityStrip — W55 Hero band component. W66.E: canonical bucket import.
 *
 * "Pipeline Reality — Commit / Upside / Best Case / Pipeline / Omit — W66.E canon"
 * with counts and subtitle about qualification floor.
 *
 * Data: accounts table, forecast bucket computed from composite_score via
 * account_plans. Bucket logic imported from client/src/lib/forecast-buckets.
 *
 * W66.E: removed local bucketFromScore and BUCKET_CONFIG — imported from canonical module.
 *
 * Tenant 1 only. SIM/SMOKE_TEST excluded.
 */

import { useEffect, useState } from "react";
import { supabase, getCurrentTenant } from "@/lib/session";
import { QUALIFICATION_FLOOR_PCT } from "@/data/marketing-targets";
import {
  bucketForScore,
  BUCKET_ORDER,
  type ForecastBucket,
} from "@/lib/forecast-buckets";

const TENANT_ID = 1;

type BucketCounts = Record<ForecastBucket, number>;

function emptyBuckets(): BucketCounts {
  return Object.fromEntries(
    BUCKET_ORDER.map((b) => [b, 0]),
  ) as BucketCounts;
}

export function PipelineRealityStrip() {
  const [counts, setCounts] = useState<BucketCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const tenant = getCurrentTenant();

  useEffect(() => {
    const tenantId = tenant?.tenantId ?? TENANT_ID;
    let cancelled = false;
    setLoading(true);

    supabase()
      .from("accounts")
      .select(`
        id,
        name,
        account_plans!left(composite_score)
      `)
      .eq("tenant_id", tenantId)
      .in("status", ["Suspect", "Prospect", "Qualified Opp", "Full Opportunity"])
      .not("name", "ilike", "[SIM-%")
      .not("name", "ilike", "SMOKE_TEST_%")
      .then(({ data }) => {
        if (cancelled) return;
        const buckets = emptyBuckets();
        if (Array.isArray(data)) {
          for (const a of data) {
            const score =
              Array.isArray(a.account_plans) && a.account_plans.length > 0
                ? a.account_plans[0].composite_score
                : (a.account_plans as any)?.composite_score ?? null;
            // No hard-stop signal at account level — score-only assignment.
            const bucket = bucketForScore(score, false);
            buckets[bucket]++;
          }
        }
        setCounts(buckets);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tenant?.tenantId]);

  if (loading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-4 animate-pulse">
        <div className="flex gap-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-14 w-20 bg-neutral-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  const total = counts ? Object.values(counts).reduce((s, n) => s + n, 0) : 0;
  const aboveOmitted = counts ? total - (counts["Omit"] ?? 0) : 0;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">
          Pipeline Reality
        </span>
        <span className="text-[11px] text-neutral-400">
          {aboveOmitted} of {total} accounts above Omit &middot;{" "}
          {QUALIFICATION_FLOOR_PCT}% qualification floor
        </span>
      </div>

      <div className="flex flex-wrap gap-3">
        {BUCKET_ORDER.map((key) => (
          <div key={key} className="flex flex-col items-center">
            <span className="text-2xl font-bold tabular-nums text-neutral-900">
              {counts?.[key] ?? 0}
            </span>
            <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-widest text-neutral-500">
              {key}
            </span>
          </div>
        ))}
      </div>

      <p className="mt-3 text-[11px] text-neutral-400">
        What your funnel produced. {aboveOmitted} of {total} accounts above Omit
        &middot; {QUALIFICATION_FLOOR_PCT}% qualification floor.
      </p>
    </div>
  );
}
